import React, { useEffect, useRef, useState } from "react";
import { EMAIL_MAX_LENGTH, isValidSelfServeEmail } from "../../../utils/self.serve.email";
import { FORGOT_INVALID_EMAIL_MESSAGE } from "./self.serve.alert";

/** Props for {@link ClassLandingForgotCodeForm}. */
interface ClassLandingForgotCodeFormProps {
  /** Fixed copy under the email input for the last failed submit, or "". */
  submitError: string;
  /** Drops that message once the student edits the email. */
  onClearSubmitError: () => void;
  /** Called with the trimmed email once it passes validation. Resolves when the request ends. */
  onSubmit: (email: string) => Promise<void>;
}

/**
 * "Forgot your code?" tab: one email field and a submit button.
 * A value that is blank, not an email, or longer than 254 characters shows the
 * validation message and is not sent. The parent sends the request, shows the
 * acknowledgment, and passes back `submitError` for a failure.
 */
const ClassLandingForgotCodeForm: React.FC<ClassLandingForgotCodeFormProps> = ({
  submitError,
  onClearSubmitError,
  onSubmit,
}) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const emailRef = useRef<HTMLInputElement | null>(null);

  /** Client-side validation wins; the parent's fixed failure copy shows once the email is valid. */
  const emailMessage = emailError || submitError;

  // Focus the field once a message is showing and the input is enabled again.
  useEffect(() => {
    if (emailMessage && !submitting) {
      emailRef.current?.focus();
    }
  }, [emailMessage, submitting]);

  /** Validates, then calls the parent. A second click while the request is in flight does nothing. */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittingRef.current) {
      return;
    }
    const trimmedEmail = email.trim();
    if (!isValidSelfServeEmail(trimmedEmail)) {
      setEmailError(FORGOT_INVALID_EMAIL_MESSAGE);
      return;
    }
    setEmailError("");
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onSubmit(trimmedEmail);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate={true} aria-busy={submitting}>
      <p className="text-muted" style={{ fontSize: 14 }}>
        Enter the email you used for this class.
      </p>
      <div className="form-group">
        <label className="required" htmlFor="ss-forgot-email">Email</label>
        <input
          id="ss-forgot-email"
          ref={emailRef}
          type="email"
          maxLength={EMAIL_MAX_LENGTH}
          className={`form-control ${emailMessage ? "is-invalid" : ""}`}
          autoComplete="email"
          placeholder="Email Address"
          value={email}
          disabled={submitting}
          aria-required="true"
          aria-invalid={Boolean(emailMessage)}
          aria-describedby={emailMessage ? "ss-forgot-email-error" : undefined}
          onChange={(event) => {
            setEmail(event.target.value);
            setEmailError("");
            if (submitError) {
              onClearSubmitError();
            }
          }}
        />
        {emailMessage ? (
          <div id="ss-forgot-email-error" className="invalid-feedback" role="alert">
            {emailMessage}
          </div>
        ) : null}
      </div>
      <div className="ss-public-actions">
        <button
          type="submit"
          className="edsimsV2-pill-button edsimsV2-pill-button--primary"
          disabled={submitting}
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default ClassLandingForgotCodeForm;
